/**
 * Tenant create/edit — one component for both `new` and `:id/edit`, switched on
 * whether `useParams().id` is present (same shape as PropertyFormPage).
 * Create uses `POST tenants/tenants/`, edit uses `PATCH tenants/tenants/{id}/`.
 *
 * ⚠ On CREATE, if the password field is left blank, the backend defaults the
 * account password to the literal `password123` for every tenant created this
 * way — a pre-existing, ported-as-is behaviour (see `TENANT_DEFAULT_PASSWORD` in
 * the backend). That is surfaced to the caller here rather than hidden, per the
 * explicit scope note for this page. There is no password field on EDIT: the
 * tenant PATCH serializer has no writable password, and changing a password goes
 * through `auth/change-password/`.
 *
 * ROLE-DEPENDENT PREFILL: `GET tenants/tenants/{id}/` returns one of two shapes
 * (see `isEnhancedTenant` in src/types/tenant.ts). AGENT/SUPERAGENT callers get
 * the enhanced shape, which carries only `full_name` and no username — so those
 * fields cannot be prefilled, and the backend ignores writes to them from
 * exactly those callers anyway (`updateTenant` in tenants/routes.ts). LANDLORD
 * callers get the plain `User` shape and can edit all of them.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { createTenant, getTenant, updateTenant } from '../../api/endpoints/tenants';
import { isEnhancedTenant } from '../../types/tenant';
import type { TenantCreateRequest, TenantPatchRequest } from '../../types/tenant';

const INITIAL_FORM = {
  email: '',
  username: '',
  first_name: '',
  last_name: '',
  phone: '',
  password: '',
};

type FormState = typeof INITIAL_FORM;

const DEFAULT_PASSWORD = 'password123';

function toCreatePayload(form: FormState): TenantCreateRequest {
  return {
    email: form.email,
    username: form.username,
    first_name: form.first_name,
    last_name: form.last_name,
    phone: form.phone,
    password: form.password === '' ? undefined : form.password,
  };
}

/**
 * `namesEditable` is false when the fetched tenant came back in the enhanced
 * shape: those fields were never prefilled, so sending them would PATCH blanks
 * over real values if the backend ever started honouring them for this caller.
 */
function toPatchPayload(form: FormState, namesEditable: boolean): TenantPatchRequest {
  const payload: TenantPatchRequest = { email: form.email, phone: form.phone };
  if (namesEditable) {
    payload.username = form.username;
    payload.first_name = form.first_name;
    payload.last_name = form.last_name;
  }
  return payload;
}

export function TenantFormPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const tenantId = Number(id);
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const existing = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => getTenant(tenantId),
    enabled: isEdit && Number.isFinite(tenantId),
  });

  // False only while editing a tenant the server described with the enhanced
  // serializer — see the ROLE-DEPENDENT PREFILL note above.
  const namesEditable = !isEdit || existing.data === undefined || !isEnhancedTenant(existing.data);

  useEffect(() => {
    if (existing.data === undefined) return;
    const tenant = existing.data;

    if (isEnhancedTenant(tenant)) {
      // enhanced tenant rows have no first/last name split, only full_name —
      // leave blank rather than guess-split it
      setForm({ ...INITIAL_FORM, email: tenant.email, phone: tenant.phone });
      return;
    }

    setForm({
      ...INITIAL_FORM,
      email: tenant.email,
      username: tenant.username,
      first_name: tenant.first_name,
      last_name: tenant.last_name,
      phone: tenant.phone,
    });
  }, [existing.data]);

  function update(field: keyof FormState, value: string): void {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  const save = useMutation({
    // Annotated because the two branches return different shapes (`TenantRow` vs
    // `CreatedTenant`); only the id is used, and both carry one.
    mutationFn: async (): Promise<{ id: number }> =>
      isEdit
        ? updateTenant(tenantId, toPatchPayload(form, namesEditable))
        : createTenant(toCreatePayload(form)),
    // 'new' and ':id/edit' are both siblings of ':id' under 'tenants', so a
    // relative '..' lands on 'tenants' from 'new' and on 'tenants/:id' from
    // ':id/edit' — the edit case is already at the right target.
    onSuccess: (tenant) => navigate(isEdit ? '..' : `../${tenant.id}`, { replace: true }),
    onError: (caught) => {
      const parsed = parseApiError(caught);
      setError(parsed.message);
      setFieldErrors(parsed.fieldErrors);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    save.mutate();
  }

  function fieldError(field: string): string | undefined {
    return fieldErrors[field]?.[0];
  }

  if (isEdit && existing.isPending) return <p>Loading…</p>;
  if (isEdit && existing.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(existing.error).message}
      </p>
    );
  }

  return (
    <section>
      <h2>{isEdit ? 'Edit tenant' : 'Add tenant'}</h2>

      <form onSubmit={handleSubmit} noValidate>
        {error !== null && (
          <p role="alert" className="form__error">
            {error}
          </p>
        )}

        <label htmlFor="tenant-email">Email</label>
        <input
          id="tenant-email"
          type="email"
          required
          value={form.email}
          onChange={(event) => update('email', event.target.value)}
        />
        {fieldError('email') !== undefined && <p className="form__field-error">{fieldError('email')}</p>}

        <label htmlFor="tenant-username">Username</label>
        <input
          id="tenant-username"
          required={!isEdit}
          disabled={!namesEditable}
          value={form.username}
          onChange={(event) => update('username', event.target.value)}
        />
        {fieldError('username') !== undefined && (
          <p className="form__field-error">{fieldError('username')}</p>
        )}

        <label htmlFor="tenant-first-name">First name</label>
        <input
          id="tenant-first-name"
          disabled={!namesEditable}
          value={form.first_name}
          onChange={(event) => update('first_name', event.target.value)}
        />

        <label htmlFor="tenant-last-name">Last name</label>
        <input
          id="tenant-last-name"
          disabled={!namesEditable}
          value={form.last_name}
          onChange={(event) => update('last_name', event.target.value)}
        />

        {!namesEditable && (
          <p role="note">
            Your role sees this tenant&apos;s name as a single combined field, so the name and
            username are not editable here. Their email and phone are.
          </p>
        )}

        <label htmlFor="tenant-phone">Phone</label>
        <input
          id="tenant-phone"
          type="tel"
          value={form.phone}
          onChange={(event) => update('phone', event.target.value)}
        />

        {!isEdit && (
          <>
            <label htmlFor="tenant-password">Password (optional)</label>
            <input
              id="tenant-password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(event) => update('password', event.target.value)}
            />
            <p role="note">
              {form.password === ''
                ? `Left blank: the account password will default to "${DEFAULT_PASSWORD}". Share it with the tenant and have them change it after first login.`
                : 'A custom password will be set for this account.'}
            </p>
          </>
        )}

        <button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : isEdit ? 'Save' : 'Create tenant'}
        </button>
      </form>
    </section>
  );
}

export default TenantFormPage;
